import { AppState, Platform, type AppStateStatus } from 'react-native';
import { getClient } from '../api/client';
import { useAuthStore } from './authStore';
import { useOfflineQueue, type QueuedMutation } from './offlineQueueStore';

const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 5 * 60 * 1000;

let started = false;
let generation = 0;
let retryAttempt = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let timerDueAt = 0;
let replayInFlight = false;
let replayRequestedWhileRunning = false;
let removeAppStateListener: (() => void) | null = null;
let removeQueueListener: (() => void) | null = null;
let removeAuthListener: (() => void) | null = null;
let webOnlineListener: (() => void) | null = null;

function currentOwnerId(): string | null {
  const ownerId = useAuthStore.getState().user?.waId?.trim();
  return ownerId || null;
}

function hasQueueForOwner(ownerId: string): boolean {
  return useOfflineQueue.getState().queue.some(m => m.ownerId === ownerId);
}

async function executeMutation(mutation: QueuedMutation): Promise<void> {
  const client = getClient();
  if (mutation.method === 'delete') {
    await client.delete(mutation.url);
  } else if (mutation.method === 'put') {
    await client.put(mutation.url, mutation.data);
  } else {
    await client.post(mutation.url, mutation.data);
  }
}

function backoffDelay(): number {
  const delay = Math.min(RETRY_BASE_MS * (2 ** retryAttempt), RETRY_MAX_MS);
  retryAttempt += 1;
  return delay;
}

async function replayQueuedMutations(runGeneration: number): Promise<void> {
  timer = null;
  timerDueAt = 0;
  if (!started || runGeneration !== generation || replayInFlight) return;

  const ownerId = currentOwnerId();
  if (!ownerId) return;

  replayInFlight = true;
  replayRequestedWhileRunning = false;

  let result = { ok: 0, failed: 0 };
  try {
    result = await useOfflineQueue.getState().flush(ownerId, executeMutation);

    if (result.ok > 0 && started && runGeneration === generation && currentOwnerId() === ownerId) {
      // Import lazily to avoid a module cycle: reminderStore enqueues mutations
      // and also asks this replay service to run.
      const { useRemindersStore } = await import('./reminderStore');
      await useRemindersStore.getState().syncFromServer(true);
    }
  } catch (error) {
    console.warn('[OfflineQueue] Replay failed:', error);
    result = { ok: 0, failed: 1 };
  } finally {
    replayInFlight = false;
  }

  if (!started) return;
  if (runGeneration !== generation) {
    // A new authenticated lifecycle started while the previous request was
    // winding down. Give the current owner its own replay pass now.
    replayRequestedWhileRunning = false;
    requestOfflineQueueReplay(0);
    return;
  }

  const activeOwnerId = currentOwnerId();
  if (!activeOwnerId || !hasQueueForOwner(activeOwnerId)) {
    retryAttempt = 0;
    replayRequestedWhileRunning = false;
    return;
  }

  if (replayRequestedWhileRunning || (result.ok > 0 && result.failed === 0)) {
    replayRequestedWhileRunning = false;
    retryAttempt = 0;
    requestOfflineQueueReplay(0);
    return;
  }

  replayRequestedWhileRunning = false;
  requestOfflineQueueReplay(backoffDelay());
}

/** Ask the singleton replay service to run, preferring the earliest request. */
export function requestOfflineQueueReplay(delayMs = 0): void {
  if (!started) return;

  if (replayInFlight) {
    replayRequestedWhileRunning = true;
    return;
  }

  const dueAt = Date.now() + Math.max(0, delayMs);
  if (timer && timerDueAt <= dueAt) return;
  if (timer) clearTimeout(timer);

  timerDueAt = dueAt;
  const runGeneration = generation;
  timer = setTimeout(() => {
    void replayQueuedMutations(runGeneration);
  }, Math.max(0, delayMs));
}

/**
 * Start reliable replay for the authenticated app session. Recovery triggers:
 * queue enqueue/hydration, app foreground, browser online, WS reconnect, and
 * bounded exponential retry while owned mutations remain queued.
 */
export function startOfflineQueueReplay(): () => void {
  stopOfflineQueueReplay();
  started = true;
  generation += 1;

  const appStateSubscription = AppState.addEventListener('change', (state: AppStateStatus) => {
    if (state === 'active') requestOfflineQueueReplay(0);
  });
  removeAppStateListener = () => appStateSubscription.remove();

  removeQueueListener = useOfflineQueue.subscribe((state, previousState) => {
    const previousIds = new Set(previousState.queue.map(m => m.id));
    const addedForCurrentOwner = state.queue.some(m =>
      !previousIds.has(m.id) && m.ownerId === currentOwnerId()
    );
    // An AsyncStorage hydration may also contain unowned legacy entries. Run
    // once so flush can discard those entries safely.
    const addedLegacyEntry = state.queue.some(m => !previousIds.has(m.id) && !m.ownerId);
    if (addedForCurrentOwner || addedLegacyEntry) requestOfflineQueueReplay(0);
  });

  removeAuthListener = useAuthStore.subscribe((state, previousState) => {
    if (state.user?.waId !== previousState.user?.waId) {
      retryAttempt = 0;
      requestOfflineQueueReplay(0);
    }
  });

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    webOnlineListener = () => requestOfflineQueueReplay(0);
    window.addEventListener('online', webOnlineListener);
  }

  requestOfflineQueueReplay(0);
  return stopOfflineQueueReplay;
}

export function stopOfflineQueueReplay(): void {
  started = false;
  generation += 1;
  retryAttempt = 0;
  replayRequestedWhileRunning = false;

  if (timer) clearTimeout(timer);
  timer = null;
  timerDueAt = 0;

  removeAppStateListener?.();
  removeQueueListener?.();
  removeAuthListener?.();
  removeAppStateListener = null;
  removeQueueListener = null;
  removeAuthListener = null;

  if (webOnlineListener && Platform.OS === 'web' && typeof window !== 'undefined') {
    window.removeEventListener('online', webOnlineListener);
  }
  webOnlineListener = null;
}
